import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, SelectionList, Text, } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo, useRef, useState, } from 'react';
import { useDebounce } from 'react-use';
import { analytics } from 'app/utils/segment';
import { useAnalyticsState } from 'app/views/layout/views/analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { convertTreeToFlatItems } from 'utils/tree';
import { isOptionalOperator } from '../../../../../../utils/filters/isOptionalOperator';
import { AnalyticsFilterItem, AnalyticsFilterLayout, AnalyticsFilterOptions, AnalyticsFilterSearch, } from '../../../../../filter';
import styles from '../../analyticsCustomAttributesFilter.module.scss';
import { customAttributesFilterAssets } from '../../utils/customAttributesFilterAssets';
const { operators } = customAttributesFilterAssets['select'];
export const AnalyticsCustomAttributesFilterSelect = memo(function AnalyticsCustomAttributesFilterSelect({ field, attribute, attribute: { options }, selectedValues = [], defaultOperator = operators[0], selectedOperator = defaultOperator, isDropdownOpen, onChange, onBack, }) {
    const { dashboard } = useAnalyticsState();
    const trackingType = `${field}/${attribute.type}`;
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);
    const searchRef = useRef(null);
    const isSelectionDisabled = isOptionalOperator(selectedOperator);
    /**
     * Handles changing the filter values.
     *
     * If there is at least one value selected, the filter is either added or
     * updated in the state. Otherwise if no values remain selected, the filter
     * is removed from the state. When selecting a node value, any existing
     * selected child values are automatically removed.
     */
    const handleChange = useCallback(({ selectedValues, selectedItem, }) => {
        const newValues = selectedItem.isNode && selectedItem.isSelected
            ? selectedValues.filter((value) => {
                // A value is a child value of the selected node value if it is longer than the node
                // value and has the same first items as the node value.
                const isChildValue = value.length > selectedItem.value.length &&
                    intersection(value, selectedItem.value).length > 0;
                return !isChildValue;
            })
            : selectedValues;
        onChange({
            op: newValues.length ? 'upsert' : 'remove',
            filter: {
                field,
                values: newValues.map((value) => ({ value })),
                operator: selectedOperator,
                attribute_id: attribute.id,
            },
        });
        if (selectedItem.isSelected) {
            analytics.analyticsFilterValueAdded({
                dashboard: dashboard.title,
                type: trackingType,
                value: selectedItem.value.join('/'),
                isNode: selectedItem.isNode,
            });
        }
    }, [
        attribute.id,
        dashboard.title,
        field,
        onChange,
        selectedOperator,
        trackingType,
    ]);
    /**
     * Handles changing the filter operator.
     *
     * If filter is restored to its default state, i.e. with the default
     * operator and no values selected, the filter is removed from the state.
     *
     * Otherwise if the filter operator is of the optional type, any previously
     * selected values are cleared and the filter is updated in the state.
     */
    const handleChangeOperator = useCallback((operator) => {
        const isDefaultState = operator === defaultOperator && !selectedValues.length;
        onChange({
            op: isDefaultState ? 'remove' : 'upsert',
            filter: {
                field,
                values: isOptionalOperator(operator)
                    ? []
                    : selectedValues.map((value) => ({ value })),
                operator,
                attribute_id: attribute.id,
            },
        });
        analytics.analyticsFilterOperatorUpdated({
            dashboard: dashboard.title,
            type: trackingType,
            operator,
        });
    }, [
        attribute.id,
        dashboard.title,
        defaultOperator,
        field,
        onChange,
        selectedValues,
        trackingType,
    ]);
    /** Handles remove the filter from the state. */
    const handleClear = useCallback(() => {
        onChange({
            op: 'remove',
            filter: {
                field,
                values: [],
                operator: selectedOperator,
                attribute_id: attribute.id,
            },
        });
        analytics.analyticsFilterCleared({
            dashboard: dashboard.title,
            type: trackingType,
        });
    }, [
        attribute.id,
        dashboard.title,
        field,
        onChange,
        selectedOperator,
        trackingType,
    ]);
    /**
     * The flattened list of options, where each value is represented by a path
     * of all its parents' values. Used when searching, to select leaf values.
     */
    const flatOptions = useMemo(() => convertTreeToFlatItems(options, {
        childrenKey: 'options',
        transformItem: (option, path) => (Object.assign(Object.assign({}, option), { label: path
                .map(({ label }) => label)
                .concat([option.label])
                .join(' / '), value: path.map(({ value }) => value).concat([option.value]) })),
    }).filter(({ options }) => !(options === null || options === void 0 ? void 0 : options.length)), [options]);
    /** Handles rendering of flat options when searching. */
    const renderFilteredListItems = useCallback(() => {
        const fuse = new Fuse(flatOptions, {
            keys: ['label', 'value'],
            threshold: 0.2,
            distance: 200,
        });
        const filteredOptions = fuse
            .search(debouncedSearchTerm)
            .map(({ item }) => item);
        if (!filteredOptions.length) {
            return _jsx(AnalyticsFilterLayout.Empty, {});
        }
        return filteredOptions.map((option) => (_jsx(AnalyticsFilterItem, { value: option.value, label: option.label, highlight: debouncedSearchTerm }, option.id)));
    }, [debouncedSearchTerm, flatOptions]);
    /**
     * Handles rendering a selection list item when the value is a leaf, and
     * when the value is a node and has nested child options, it renders another
     * list recursively.
     */
    const renderListOrItem = useCallback((option, parentValuePath = []) => {
        var _a;
        const valuePath = [...parentValuePath, option.value];
        if ((_a = option.options) === null || _a === void 0 ? void 0 : _a.length) {
            const isSelected = selectedValues.some((value) => isEqual(value, valuePath));
            const selectedChildValues = selectedValues.filter((value) => value.length > valuePath.length &&
                isEqual(value.slice(0, valuePath.length), valuePath));
            const parentItem = (_jsx(AnalyticsFilterItem, Object.assign({ value: valuePath, label: option.label, isDisabled: isSelectionDisabled, isIndeterminate: selectedChildValues.length > 0, contentClassName: classNames(styles.itemContent) }, { children: selectedChildValues.length > 0 && (_jsx(Badge, Object.assign({ variant: "secondary", size: "small" }, { children: selectedChildValues.length }))) }), option.id));
            // If the parent item is selected, don't render the children.
            if (isSelected)
                return parentItem;
            return (_jsx(SelectionList.List, Object.assign({ parentItem: parentItem }, { children: [...option.options].map((childOption) => renderListOrItem(childOption, valuePath)) }), option.id));
        }
        return (_jsx(AnalyticsFilterItem, { value: valuePath, label: option.label, isDisabled: isSelectionDisabled }, option.id));
    }, [isSelectionDisabled, selectedValues]);
    /** Handles rendering the selection list items recursively. */
    const renderListItems = useCallback(() => options.map((option) => renderListOrItem(option)), [options, renderListOrItem]);
    return (_jsxs(_Fragment, { children: [_jsx(AnalyticsFilterLayout.Row, Object.assign({ borderBottom: true }, { children: _jsx(AnalyticsFilterSearch, { ref: searchRef, value: searchTerm, isOpen: true, isDisabled: isSelectionDisabled, onChange: setSearchTerm }) })), _jsx(AnalyticsFilterLayout.Row, { children: _jsx(Text.Paragraph, Object.assign({ variant: "3", subdued: true }, { children: attribute.label })) }), _jsx(AnalyticsFilterLayout.Row, Object.assign({ noPadding: true, scrollable: true }, { children: _jsxs(SelectionList, Object.assign({ isMultiple: true, isActive: true, selectedValues: selectedValues, onChange: handleChange, equalityFn: isEqual, defaultActiveItemIndex: 1 }, { children: [_jsx(SelectionList.BackItem, { onSelected: onBack }), debouncedSearchTerm
                            ? renderFilteredListItems()
                            : renderListItems()] })) })), _jsx(AnalyticsFilterLayout.Row, Object.assign({ borderTop: true, slottable: true }, { children: _jsx(AnalyticsFilterOptions, { operators: operators, defaultOperator: defaultOperator, selectedOperator: selectedOperator, isMissingSelectedValues: !selectedValues.length, isDropdownOpen: isDropdownOpen, onChangeOperator: handleChangeOperator, onClear: handleClear }) }))] }));
});
